// Copyright (c) 2021 WiseTime. All rights reserved.

import {
  TimeGroup,
  TimelineMarker,
  TimeRow,
  TrashStatus,
} from "../../generated/wisetime/user/timeline/v4/timeline_pb"
import { getActivityHourLocal, getLocalTimezoneOffset } from "./dateTime"
import { ToolbarState } from "features/timeline/toolbar/slice"
import { TimeBlocksSortOrder, TimelineRowSortType } from "generated/timeline/toolbar/toolbar_pb"
import { getSortTypeStrategy, sortTagsListAlphabetically } from "./sorting"
import {
  isTimeRow,
  PostErrorMessages,
  TimeGroupFieldConfigsCache,
  TimesheetFieldConfigsCache,
  TimelineRow,
  TimeMarkerRow,
} from "./types"
import { isMatchingShowSelectedItemToggle, isMatchingToolbarAndSearchFilters } from "./filters"
import { Site } from "../../generated/legebill/site/v4/site_pb"
import { notUndefined } from "../../lib/utils"
import { isSearchNotEmpty } from "./search-bar/searchUtils"
import { t } from "@lingui/macro"
import { DateTime } from "luxon"
import { findLastIndex } from "ramda"
import { computeDurationStep, secondsToHoursRounded } from "./timeGroups"

export type TimeBlock = {
  timelineRows: TimelineRow[],
  // User local time yyyyMMddHH
  activityHour: number,
  activityHourLocalCurrentTz: number,
  activityHourLocalRecordedTz: number,
  // User's current time zone offset from UTC in hours
  timeZoneOffsetCurrent: number,
  // Recorded time zone offset from UTC in hours
  timeZoneOffsetRecorded: number,
  timeZoneRecorded: string | undefined,
}

const filterRowsForBlockAggKey = (
  source: TimeRow[],
  aggKey: ActivityHourWithOffsetCompoundKey
): TimeRow[] => {
  // Avoid duplicate time rows
  const seenTimeRowIds = new Set<string>()
  return source
    .filter(timeRow => activityHourWithOffsetCompoundKeyForRow(timeRow) === aggKey)
    .filter(timeRow => {
      if (seenTimeRowIds.has(timeRow.id)) return false
      seenTimeRowIds.add(timeRow.id)
      return true
    })
}

export const makeTimeMarkerRow = (timeMarker: TimelineMarker, timeZoneName: string): TimeMarkerRow => {
  const date = DateTime.fromSeconds(Number(timeMarker.startTime?.seconds ?? 0))
  const dateInUtc = date.toUTC()
  const activityHour = Number(dateInUtc.toFormat("yyyyMMddHH"))
  return ({
    ...timeMarker,
    activityHour,
    firstObservedInHour: dateInUtc.get("minute"),
    timeZone: timeZoneName,
    timeZoneOffset: getLocalTimezoneOffset(activityHour, timeZoneName),
  })
}
export const aggregateTimeBlocks = (
  timeRowsForDate: TimeRow[],
  timeMarkersForDate: TimeMarkerRow[],
  timeZoneName: string,
): TimeBlock[] => {
  // Avoid duplicate time blocks
  const seenTimeBlockIds = new Set<string>()
  const createTimeBlock = (
    {
      activityHour,
      timeZone,
      timeZoneOffset,
    }: TimelineRow,
    timeZoneNameFromState: string,
    timelineRows: TimelineRow[],
  ): TimeBlock => {
    const localTimezoneOffset = getLocalTimezoneOffset(activityHour, timeZoneNameFromState)
    const activityHourLocalCurrentTz = getActivityHourLocal(activityHour, localTimezoneOffset)
    return ({
      timelineRows,
      // yyyyMMddHH in UTC
      activityHour,
      // Local time yyyyMMddHH in user's current time zone
      activityHourLocalCurrentTz,
      // User local time yyyyMMddHH in the recorded time zone
      activityHourLocalRecordedTz: getActivityHourLocal(activityHour, timeZoneOffset),
      // User's current time zone offset from UTC in hours
      timeZoneOffsetCurrent: localTimezoneOffset,
      // Recorded time zone offset from UTC in hours
      timeZoneOffsetRecorded: timeZoneOffset,
      timeZoneRecorded: timeZone,
    })
  }
  const aggregatedTimeBlocks = timeRowsForDate
    .reduce((
      result: TimeBlock[],
      currTimeRow: TimeRow,
      _index: number,
      source: TimeRow[],
    ): TimeBlock[] => {
      const timeRowBlockAggKey = activityHourWithOffsetCompoundKeyForRow(currTimeRow)
      if (!result.some(timeBlock => activityHourWithOffsetCompoundKeyForBlock(timeBlock) === timeRowBlockAggKey)) {
        const rowsActivityHourLocalized = filterRowsForBlockAggKey(source, timeRowBlockAggKey)
          .map((timeRow: TimeRow) => ({
            ...timeRow,
            tags: [...timeRow.tags].sort(sortTagsListAlphabetically),
          }))
        return [
          ...result,
          createTimeBlock(currTimeRow, timeZoneName, rowsActivityHourLocalized),
        ]
      }
      return result
    }, [])

  timeMarkersForDate.forEach((marker: TimeMarkerRow): void => {
    const blockIndexes = aggregatedTimeBlocks
      .map((timeBlock, index) => timeBlock.activityHour === marker.activityHour ? index : undefined)
      .filter(notUndefined)
    if (blockIndexes.length !== 0) {
      blockIndexes.forEach(index => {
        const block = aggregatedTimeBlocks[index]
        if (block) {
          const [row] = block.timelineRows
          // time marker should have time rows time zone info for correct sorting
          block.timelineRows.push(row ? ({
            ...marker,
            timeZoneOffset: row.timeZoneOffset,
            timeZone: row.timeZone,
          }) : marker)
        }
      })
    } else {
      aggregatedTimeBlocks.push(createTimeBlock(marker, timeZoneName, [marker]))
    }
  })

  return aggregatedTimeBlocks
    .filter(timeBlock => {
      const timeBlockId = activityHourWithOffsetCompoundKeyForBlock(timeBlock)
      if (seenTimeBlockIds.has(timeBlockId)) return false
      seenTimeBlockIds.add(timeBlockId)
      return true
    })
    .map((timeBlock: TimeBlock): TimeBlock => ({
      ...timeBlock,
      timelineRows: [...timeBlock.timelineRows]
        .sort(getSortTypeStrategy(TimeBlocksSortOrder.ASCENDING, TimelineRowSortType.CHRONOLOGY))
    }))
    .sort(chronologicalWithTimeZone())
}

const chronologicalWithTimeZone = () => {
  let prevTimezone: string | undefined
  return (timeBlock1: TimeBlock, timeBlock2: TimeBlock) => {
    // sort by activity hour
    const timeZoneOffsetForBlock2 = timeBlock2.timeZoneOffsetRecorded.toString()
    if (timeBlock1.activityHourLocalCurrentTz > timeBlock2.activityHourLocalCurrentTz) {
      prevTimezone = timeZoneOffsetForBlock2
      return 1
    }
    const timeZoneOffsetForBlock1 = timeBlock1.timeZoneOffsetRecorded.toString()
    if (timeBlock1.activityHourLocalCurrentTz < timeBlock2.activityHourLocalCurrentTz) {
      prevTimezone = timeZoneOffsetForBlock1
      return -1
    }
    // if activity hours are the same, sort blocks with the respect of previous block timezone
    // so time blocks with the same time zone go side by side
    if (prevTimezone === undefined || timeZoneOffsetForBlock1 === prevTimezone) {
      prevTimezone = timeZoneOffsetForBlock1
      return -1
    }
    prevTimezone = timeZoneOffsetForBlock2
    return 1
  }
}

type ActivityHourWithOffsetCompoundKey = string
export const activityHourWithOffsetCompoundKeyForBlock = (timeBlock: TimeBlock): ActivityHourWithOffsetCompoundKey =>
  buildActivityHourCompoundKey(timeBlock.activityHourLocalRecordedTz, timeBlock.timeZoneOffsetRecorded)

const activityHourWithOffsetCompoundKeyForRow = (timeRow: TimeRow): ActivityHourWithOffsetCompoundKey =>
  buildActivityHourCompoundKey(
    getActivityHourLocal(timeRow.activityHour, timeRow.timeZoneOffset),
    timeRow.timeZoneOffset,
  )

const buildActivityHourCompoundKey = (activityHourLocalCurrentTz: number, timezoneOffset: number): ActivityHourWithOffsetCompoundKey =>
  activityHourLocalCurrentTz.toString() + "_" + timezoneOffset.toString()

export const filterTimeBlocks = (timeBlocks: TimeBlock[], activeTimeRowIds: string[], filter?: TimeBlocksFilter): TimeBlock[] => {
  const timeBlocksWithNonTrashed = timeBlocks.map(timeBlock => {
    const filteredRows = timeBlock.timelineRows.filter((timelineRow: TimelineRow) => {
      if (isTimeRow(timelineRow)) {
        return timelineRow.trashStatus !== TrashStatus.TRASHED
      }
      return true
    })
    return ({
      ...timeBlock,
      timelineRows: filteredRows,
    })
  }).filter(timeBlock => timeBlock.timelineRows.length > 0)
  if (!filter) {
    return timeBlocksWithNonTrashed
  }

  const toolbar = filter.toolbarState
  const updatedTimeBlocks = timeBlocksWithNonTrashed.map(timeBlock => {
    if (toolbar.viewFlags.length > 0 || isSearchNotEmpty(toolbar.searchTerms) || filter.showSelectedTimeRows) {
      const filteredRows = timeBlock.timelineRows.filter((timelineRow: TimelineRow) => {
        if (isTimeRow(timelineRow)) {
          return isMatchingToolbarAndSearchFilters(timelineRow, toolbar, filter.currentSiteId)
            && isMatchingShowSelectedItemToggle(timelineRow, filter.showSelectedTimeRows, activeTimeRowIds)
        }
        // Exclude timeline markers when showSelectedTimeRows is true
        return !filter.showSelectedTimeRows
      })
      return ({
        ...timeBlock,
        timelineRows: filteredRows
      })
    }
    return timeBlock
  }).filter(timeBlock => timeBlock.timelineRows.length > 0)
    .map(timeBlock => {
      if (toolbar.sortType !== TimelineRowSortType.CHRONOLOGY) {
        return ({
          ...timeBlock,
          timelineRows: [...timeBlock.timelineRows].sort(getSortTypeStrategy(toolbar.timeBlocksSortOrder, toolbar.sortType))
        })
      }
      return timeBlock
    })

  if (toolbar.timeBlocksSortOrder === TimeBlocksSortOrder.DESCENDING) {
    return updatedTimeBlocks.reverse()
  }
  return updatedTimeBlocks
}

export type TimeBlocksFilter = {
  toolbarState: ToolbarState,
  showSelectedTimeRows: boolean,
  currentSiteId: string | undefined,
}

export const getTimeBlocks = (
  timeRowsForDate: TimeRow[],
  timeZoneName: string,
  filter: TimeBlocksFilter,
  timeMarkersForDate: TimelineMarker[] | undefined = [],
  activeTimeRowIds: string[],
): TimeBlock[] => (
  filterTimeBlocks(
    aggregateTimeBlocks(
      timeRowsForDate,
      timeMarkersForDate.map(timeMarker => makeTimeMarkerRow(timeMarker, timeZoneName)),
      timeZoneName,
    ),
    activeTimeRowIds,
    filter,
  )
)

export const getTimeRowsDurationSecs = (timeRows: TimeRow[]): number =>
  timeRows.reduce((result: number, timeRow: TimeRow) => {
    if (timeRow.trashStatus !== TrashStatus.TRASHED) {
      return result + Number(timeRow.duration?.seconds ?? 0)
    }
    return result
  }, 0)

export const getTrashedTimeRowsDurationSecs = (timeRows: TimeRow[]): number =>
  timeRows.reduce((result: number, timeRow: TimeRow) => {
    if (timeRow.trashStatus === TrashStatus.TRASHED) {
      return result + Number(timeRow.duration?.seconds ?? 0)
    }
    return result
  }, 0)

export const getTimeBlockDurationSecs = (timeRows: TimeRow[]): number =>
  Math.min(getTimeRowsDurationSecs(timeRows), 3600)

const isTagRequired = (currentSite: Site) => currentSite.postTimeRequirements?.tagRequired ?? false

export const isTimeRequiringTag = (timeGroup: TimeGroup, currentSite: Site): boolean => {
  const hasTagFromCurrentSite = timeGroup.tags.some(tag => tag.siteId === currentSite.id)
  return isTagRequired(currentSite) && !hasTagFromCurrentSite
}

export type TimesheetFieldValidationResult = {
  hasRequiredFieldsMissing: boolean,
  hasArchivedValues: boolean,
  hasNoLongerAvailableValues: boolean,
}

export type IsTimeRequiringTimesheetField = {
  timeGroup: TimeGroup,
  currentSite: Site,
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
}

/**
 * Validates timesheet field requirements for a time group and returns granular validation results.
 * 
 * This function performs comprehensive validation of timesheet fields associated with a time group,
 * checking for three distinct types of validation issues that prevent successful time posting.
 * 
 * @param params - Validation parameters including time group, site, and field configurations
 * @returns TimesheetFieldValidationResult with three boolean flags indicating validation issues
 * 
 * Validation Scenarios:
 * 
 * 1. Required Fields Missing (hasRequiredFieldsMissing: true)
 *    - Trigger: A required timesheet field has no selected value
 *    - Error Message: "Complete all required timesheet fields"
 *    - Example: User must select a project from a required project dropdown but hasn't made a selection
 * 
 * 2. Archived Values Selected (hasArchivedValues: true)
 *    - Trigger: User has selected a timesheet field option that has been archived
 *    - Error Message: "Select a different value for timesheet field options that have been archived"
 *    - Example: User selected "Project Alpha" but it was later archived; they need to choose an active project
 * 
 * 3. No Longer Available Values (hasNoLongerAvailableValues: true)
 *    - Trigger: User has selected a timesheet field option that no longer exists in the configuration
 *    - Error Message: "Select a different value for timesheet field options that are no longer available"
 *    - Example: User selected "Department X" but it was completely removed from the system
 * 
 * Return Value:
 * Returns all flags as false when:
 *   - No field configurations exist for the time group
 *   - All required fields have valid, active selections
 *   - All non-required fields either have no selection or valid, active selections
 * 
 * Multiple Issues:
 * The function can return multiple flags as true simultaneously when a time group
 * has various validation issues across different fields.
 */
export const isTimeRequiringTimesheetField = ({
  timeGroup,
  currentSite,
  timesheetFieldConfigsCache,
  timeGroupFieldConfigsCache,
}: IsTimeRequiringTimesheetField): TimesheetFieldValidationResult => {
  const configHashList = timeGroupFieldConfigsCache[timeGroup.id] ?? []
  let hasRequiredFieldsMissing = false
  let hasArchivedValues = false
  let hasNoLongerAvailableValues = false

  configHashList.forEach(hash => {
    const timesheetFieldConfig = timesheetFieldConfigsCache[hash]
    if (!timesheetFieldConfig) {
      return
    }

    const timesheetFieldId = timesheetFieldConfig.configRef?.timesheetFieldId
    const selectedTimesheetFieldOption = timeGroup.selectedTimesheetFieldOptions.find(
      option => option.siteId === currentSite.id && option.timesheetFieldId === timesheetFieldId
    )

    if (!selectedTimesheetFieldOption && timesheetFieldConfig.isRequiredField) {
      hasRequiredFieldsMissing = true
    }

    if (!selectedTimesheetFieldOption) {
      return
    }

    const selectedOptionFromConfig = timesheetFieldConfig.options.find(option =>
      option.id === selectedTimesheetFieldOption.timesheetFieldOptionId
    )

    if (!selectedOptionFromConfig) {
      hasNoLongerAvailableValues = true
    }
    if (selectedOptionFromConfig?.archived) {
      hasArchivedValues = true
    }
  })

  return {
    hasRequiredFieldsMissing,
    hasArchivedValues,
    hasNoLongerAvailableValues,
  }
}

export const isTimeRequiringResolveTagCategory = (timeGroup: TimeGroup, currentSite: Site): boolean => (
  timeGroup.tagCategories.some(tagCategory => tagCategory.siteId === currentSite.id)
)

export const isGroupRequiringDuration = (
  timeGroup: TimeGroup,
  roundingPreferenceSeconds: number,
): boolean =>
  secondsToHoursRounded(
    Number(timeGroup.totalDuration?.seconds ?? 0),
    roundingPreferenceSeconds,
  ) < computeDurationStep(roundingPreferenceSeconds)

export type PostValidationResult = {
  errorMessages: PostErrorMessages,
  snackBarError: string | undefined,
  postableRowIds: string[],
}

type TimeValidationErrorKeys = "REQUIRING_TAG"
  | "REQUIRING_TIMESHEET_FIELD_REQUIRED"
  | "REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_ARCHIVED"
  | "REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_NO_LONGER_AVAILABLE"
  | "REQUIRING_RESOLVE_TAG_CATEGORY"
export const timeValidationErrorMessages: Record<TimeValidationErrorKeys, string> = {
  REQUIRING_TAG: t({
    id: "msg_post_time_assign_a_tag",
    message: "Assign a tag",
  }),
  REQUIRING_TIMESHEET_FIELD_REQUIRED: t({
    id: "msg_post_time_complete_required_timesheet_fields",
    message: "Complete all required timesheet fields",
  }),
  REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_ARCHIVED: t({
    id: "msg_post_time_replace_selected_archived_value_in_timesheet_fields",
    message: "Select a different value for timesheet field options that have been archived",
  }),
  REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_NO_LONGER_AVAILABLE: t({
    id: "msg_post_time_replace_selected_no_longer_available_value_in_timesheet_fields",
    message: "Select a different value for timesheet field options that are no longer available",
  }),
  REQUIRING_RESOLVE_TAG_CATEGORY: t({
    id: "msg_post_time_resolve_tag_category",
    message: "Choose a tag from the tag category or remove it",
  }),
}

export const listItemPrefix = "- "

/**
 * Generates a dynamic duration requirement error message based on the rounding preference.
 * Creates a localized error message showing the minimum duration required for time groups.
 * 
 * @param roundingPreferenceSeconds - Rounding preference in seconds (e.g., 360 for 6-minute increments)
 * @returns Localized error message indicating minimum required duration
 * 
 * @example
 * getDurationErrorMessage(360) // "Timesheet duration should be at least 0.1 hours"
 * getDurationErrorMessage(900) // "Timesheet duration should be at least 0.25 hours"
 */
export const getDurationErrorMessage = (roundingPreferenceSeconds: number): string => {
  const minHours = computeDurationStep(roundingPreferenceSeconds)
  return t({
    id: "msg_invalid_group_duration",
    message: "Timesheet duration should be at least {minHours} hours",
    values: { minHours },
  })
}
export const errorMessageOfPostingTime = (
  timeGroup: TimeGroup,
  currentSite: Site,
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
): string | undefined => {
  const requiringTag = isTimeRequiringTag(timeGroup, currentSite)
  const timesheetFieldValidation = isTimeRequiringTimesheetField({
    timeGroup,
    currentSite,
    timesheetFieldConfigsCache,
    timeGroupFieldConfigsCache,
  })
  const requiringResolveTagCategory = isTimeRequiringResolveTagCategory(timeGroup, currentSite)
  const requiringDuration = isGroupRequiringDuration(timeGroup, currentSite.roundingPreferenceSec)
  const messageStart = t({ id: "msg_post_group_tip_error_message_start", message: "To post this timesheet, you need to:" })

  const hasAnyErrors = requiringTag || 
    timesheetFieldValidation.hasRequiredFieldsMissing || 
    timesheetFieldValidation.hasArchivedValues || 
    timesheetFieldValidation.hasNoLongerAvailableValues ||
    requiringResolveTagCategory || 
    requiringDuration

  if (hasAnyErrors) {
    return (
      `${messageStart}\n` +
      `${requiringTag ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TAG}\n` : ""}` +
      `${timesheetFieldValidation.hasRequiredFieldsMissing ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_REQUIRED}\n` : ""}` +
      `${timesheetFieldValidation.hasArchivedValues ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_ARCHIVED}\n` : ""}` +
      `${timesheetFieldValidation.hasNoLongerAvailableValues ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_NO_LONGER_AVAILABLE}\n` : ""}` +
      `${requiringResolveTagCategory ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_RESOLVE_TAG_CATEGORY}\n` : ""}` +
      `${requiringDuration ? `${listItemPrefix}${getDurationErrorMessage(currentSite.roundingPreferenceSec)}\n` : ""}`
    )
  }
  return undefined
}

export const snackBarErrorOfPostingTime = (
  timeGroups: TimeGroup[],
  currentSite: Site,
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
): string | undefined => {
  const requiringTag = timeGroups.some(t => isTimeRequiringTag(t, currentSite))

  // Aggregate timesheet field validation results across all time groups
  let hasRequiredFieldsMissing = false
  let hasArchivedValues = false
  let hasNoLongerAvailableValues = false

  timeGroups.forEach(timeGroup => {
    const validation = isTimeRequiringTimesheetField({
      timeGroup,
      currentSite,
      timesheetFieldConfigsCache,
      timeGroupFieldConfigsCache,
    })
    if (validation.hasRequiredFieldsMissing) hasRequiredFieldsMissing = true
    if (validation.hasArchivedValues) hasArchivedValues = true
    if (validation.hasNoLongerAvailableValues) hasNoLongerAvailableValues = true
  })

  const requiringResolveTagCategory = timeGroups.some(t => isTimeRequiringResolveTagCategory(t, currentSite))
  const requiringDuration = timeGroups.some(t => isGroupRequiringDuration(t, currentSite.roundingPreferenceSec))
  const messageStart = t({
    id: "msg_post_group_snackbar_error_message_start",
    message: "Unable to post some of the timesheets. You may need to:",
  })

  const hasAnyErrors = requiringTag || 
    hasRequiredFieldsMissing || 
    hasArchivedValues || 
    hasNoLongerAvailableValues ||
    requiringResolveTagCategory || 
    requiringDuration

  if (hasAnyErrors) {
    return (
      `${messageStart}\n` +
      `${requiringTag ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TAG}\n` : ""}` +
      `${hasRequiredFieldsMissing ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_REQUIRED}\n` : ""}` +
      `${hasArchivedValues ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_ARCHIVED}\n` : ""}` +
      `${hasNoLongerAvailableValues ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_TIMESHEET_FIELD_SELECTED_OPTION_NO_LONGER_AVAILABLE}\n` : ""}` +
      `${requiringResolveTagCategory ? `${listItemPrefix}${timeValidationErrorMessages.REQUIRING_RESOLVE_TAG_CATEGORY}\n` : ""}` +
      `${requiringDuration ? `${listItemPrefix}${getDurationErrorMessage(currentSite.roundingPreferenceSec)}\n` : ""}`
    )
  }
  return undefined
}

export const isValidationError = (errorMessage: string): boolean => (
  Object.values(timeValidationErrorMessages).some(message => errorMessage.includes(message))
)

export const validateTimeGroupsForPost = (
  currentSite: Site,
  timeGroups: TimeGroup[],
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
): PostValidationResult => {
  const errorMessages: PostErrorMessages = {}
  const postableRowIds: string[] = []

  timeGroups.forEach(timeGroup => {
    const errorMessage = errorMessageOfPostingTime(
      timeGroup,
      currentSite,
      timesheetFieldConfigsCache,
      timeGroupFieldConfigsCache,
    )

    if (errorMessage) {
      errorMessages[timeGroup.id] = errorMessage
    } else {
      postableRowIds.push(timeGroup.id)
    }
  })

  return ({
    errorMessages,
    snackBarError: snackBarErrorOfPostingTime(
      timeGroups,
      currentSite,
      timesheetFieldConfigsCache,
      timeGroupFieldConfigsCache,
    ),
    postableRowIds,
  })
}

export const countUntrashedRows = (timeRows: TimeRow[]): number => (
  timeRows.filter(timeRow => timeRow.trashStatus !== TrashStatus.TRASHED).length
)

export const countTrashedRows = (timeRows: TimeRow[]): number => (
  timeRows.filter(timeRow => timeRow.trashStatus === TrashStatus.TRASHED).length
)

export const getRowsIdsToSelect = (
  allIdsList: string[],
  activeIdsList: string[],
  idToSelect: string,
): string[] => {
  const lastSelectedIf = activeIdsList[activeIdsList.length - 1]
  if (lastSelectedIf !== undefined) {
    const startIndex = allIdsList.indexOf(lastSelectedIf)
    const endIndex = allIdsList.indexOf(idToSelect)
    const isDownDirection = startIndex < endIndex
    return (
      isDownDirection
        ? allIdsList.slice(startIndex + 1, endIndex + 1)
        : allIdsList.slice(endIndex, startIndex)
    )
      .filter(id => !activeIdsList.includes(id))
  }
  return [idToSelect]
}

/**
 * Returns IDs of time rows before the marker (until previous marker or start of hour block).
 * Used for "Select rows above" marker action.
 */
export const getRowIdsBeforeMarker = (
  allItems: TimelineRow[],
  markerId: string,
): string[] => {
  const targetIndex = allItems.findIndex(item => !isTimeRow(item) && item.id === markerId)
  if (targetIndex < 0) return []

  const itemsBeforeTarget = allItems.slice(0, targetIndex)
  const prevMarkerIndex = findLastIndex((item: TimelineRow) => !isTimeRow(item), itemsBeforeTarget)
  const startIndex = prevMarkerIndex < 0 ? 0 : prevMarkerIndex + 1

  return allItems
    .slice(startIndex, targetIndex)
    .filter(isTimeRow)
    .map(row => row.id)
}

/**
 * Returns IDs of time rows after the marker (until next marker or end of hour block).
 * Used for "Select rows below" marker action.
 */
export const getRowIdsAfterMarker = (
  allItems: TimelineRow[],
  markerId: string,
): string[] => {
  const targetIndex = allItems.findIndex(item => !isTimeRow(item) && item.id === markerId)
  if (targetIndex < 0) return []

  const itemsAfterTarget = allItems.slice(targetIndex + 1)
  const nextMarkerIndex = itemsAfterTarget.findIndex(item => !isTimeRow(item))
  const endIndex = nextMarkerIndex < 0 ? itemsAfterTarget.length : nextMarkerIndex

  return itemsAfterTarget
    .slice(0, endIndex)
    .filter(isTimeRow)
    .map(row => row.id)
}

export const validateErroredTimeGroups = (
  timeGroups: TimeGroup[],
  currentSite: Site,
  timesheetFieldConfigsCache: TimesheetFieldConfigsCache,
  timeGroupFieldConfigsCache: TimeGroupFieldConfigsCache,
): {
  postErrorMessages: PostErrorMessages,
  clearPostErrorsList: string[],
} => {
  const postErrorMessages: PostErrorMessages = {}
  const clearPostErrorsList: string[] = []
  timeGroups.forEach(timeGroup => {
    const latestErrorMessage = errorMessageOfPostingTime(
      timeGroup,
      currentSite,
      timesheetFieldConfigsCache,
      timeGroupFieldConfigsCache,
    )
    if (latestErrorMessage === undefined && timeGroup.errorMessage !== latestErrorMessage) {
      clearPostErrorsList.push(timeGroup.id)
    } else {
      postErrorMessages[timeGroup.id] = latestErrorMessage
    }
  })
  return {
    postErrorMessages,
    clearPostErrorsList,
  }
}
